import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Product } from '@nhanho/shared'
import { formatVND } from '@nhanho/shared'
import type { Post } from '@nhanho/shared'
import { products, posts, categories, media, views, explainError, type ProductStats, type StatGroup, type TopView } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useToast } from '../lib/toast'
import { PageHeader } from '../components/ui/PageHeader'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Spinner } from '../components/ui/Spinner'
import { BadgeMask } from '../components/product/BadgeMask'

interface Counts { posts: number; categories: number; media: number }

/**
 * Tổng quan. Số liệu sản phẩm lấy từ `GET /products/stats` (một aggregate) — trước đây
 * dashboard tải **toàn bộ** 5 collection (`limit=0`) chỉ để đọc 4 con số, và cảnh báo
 * chỉ tính trên 50 sản phẩm mới nhất.
 */
/**
 * Tổng quan rút gọn cho `editor` (vai thấp nhất).
 *
 * ⚠️ **Bắt buộc phải có nhánh riêng.** Bản đầy đủ gọi `products.stats()`,
 * `categories.list()`, `media.list()` trong **một** `Promise.all` — với editor thì
 * `products.stats()` trả 403, `Promise.all` reject, và cả trang Tổng quan thành đúng một
 * dòng chữ đỏ. Editor chỉ được đọc `posts` và `media`, nên chỉ hỏi đúng hai thứ đó.
 *
 * `manager` **không** rơi vào đây: từ 23/09 họ đọc được `products.stats()` và
 * `views.top()`, nên thấy bản đầy đủ.
 */
function EditorDashboard() {
  const { user } = useAuth()
  const [total, setTotal] = useState<number | null>(null)
  const [recent, setRecent] = useState<Post[]>([])

  useEffect(() => {
    Promise.all([
      posts.list({ published: 'all', limit: 1 }),
      posts.list({ published: 'all', limit: 6, sort: '-updatedAt' }),
    ])
      .then(([all, rec]) => { setTotal(all.totalDocs); setRecent(rec.docs) })
      .catch(() => { setTotal(0); setRecent([]) })
  }, [])

  return (
    <>
      <PageHeader title={`Chào ${user?.name ?? ''}`} subtitle="Khu vực viết bài của bạn" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Card title="Bài viết">
          <p className="text-2xl font-bold text-ink">{total ?? '—'}</p>
          <p className="mt-1 text-xs text-ink-muted">tổng số bài trên hệ thống</p>
          <div className="mt-3 flex gap-2">
            <Link to="/posts/new"><Button size="sm">Viết bài mới</Button></Link>
            <Link to="/posts"><Button size="sm" variant="secondary">Tất cả bài viết</Button></Link>
          </div>
        </Card>
        <Card title="Hồ sơ tác giả">
          <p className="text-sm text-ink-soft">
            Tên, ảnh, chức danh, giới thiệu và link mạng xã hội của bạn hiện ở cuối mỗi bài
            bạn viết và trên trang tác giả riêng.
          </p>
          <div className="mt-3">
            <Link to="/account"><Button size="sm" variant="secondary">Sửa hồ sơ</Button></Link>
          </div>
        </Card>
      </div>

      <Card title="Sửa gần đây" className="mt-4">
        {recent.length === 0 ? (
          <p className="text-sm text-ink-muted">Chưa có bài nào.</p>
        ) : (
          <ul className="divide-y divide-border">
            {recent.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2.5">
                <Link to={`/posts/${p.id}`} className="min-w-0 truncate text-sm text-ink hover:text-primary-700">{p.title}</Link>
                <Badge tone={p.published ? 'success' : 'neutral'}>{p.published ? 'Hiển thị' : 'Ẩn'}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  )
}

export default function Dashboard() {
  const { isManager } = useAuth()
  const toast = useToast()
  const [stats, setStats] = useState<ProductStats | null>(null)
  const [counts, setCounts] = useState<Counts | null>(null)
  const [recent, setRecent] = useState<Product[]>([])
  const [top, setTop] = useState<TopView[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Editor không có quyền ở các endpoint dưới — `EditorDashboard` lo phần của họ.
    if (!isManager) return
    Promise.all([
      products.stats(),
      posts.list({ published: 'all', limit: 1 }),
      categories.list({ limit: 1 }),
      media.list({ limit: 1 }),
      products.list({ published: 'all', limit: 6, sort: '-updatedAt', populate: 1 }),
    ]).then(([s, po, c, m, rec]) => {
      setStats(s)
      setCounts({ posts: po.totalDocs, categories: c.totalDocs, media: m.totalDocs })
      setRecent(rec.docs)
    }).catch((e) => { const { message } = explainError(e); setError(message); toast.error(message) })

    /**
     * Lượt xem nạp **riêng**, không nhét vào `Promise.all` ở trên: bảng `page_views` mới có
     * từ 11/09 nên nhiều lúc còn rỗng, và một lỗi ở đây không được làm trắng cả Tổng quan.
     */
    views.top(30, 5).then((r) => setTop(r.docs)).catch(() => setTop([]))
  }, [toast, isManager])

  // Sau mọi hook (luật hook của React), trước mọi thứ khác.
  if (!isManager) return <EditorDashboard />
  if (error && !stats) return <><PageHeader title="Tổng quan" /><p className="text-sm text-danger">{error}</p></>
  if (!stats || !counts) return <Spinner />

  const tiles = [
    { label: 'Sản phẩm', value: stats.total.count, to: '/products', sub: `${stats.published.count} hiển thị · ${stats.draft.count} ẩn` },
    { label: 'Bài viết', value: counts.posts, to: '/posts' },
    { label: 'Danh mục', value: counts.categories, to: '/categories' },
    { label: 'Hình ảnh', value: counts.media, to: '/media' },
  ]
  const labels = [
    { label: 'Nổi bật trang chủ', value: stats.featured.count, to: '/products?featured=1' },
    { label: 'Nhãn HOT', value: stats.hot.count, to: '/products?isHot=1' },
    { label: 'Nhãn MỚI', value: stats.new.count, to: '/products?isNewArrival=1' },
    { label: 'Đang giảm giá', value: stats.onSale.count, to: '/products?onSale=1' },
  ]
  const noWarn = !stats.noImage.count && !stats.noSeo.count && !stats.outOfStock.count && !stats.lowStock.count && !stats.saleExpiringIn7Days.count

  return (
    <>
      <PageHeader title="Tổng quan" actions={<Link to="/products/new"><Button>+ Sản phẩm mới</Button></Link>} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <Link key={t.to} to={t.to} className="rounded-card border border-border bg-white p-5 shadow-card transition-shadow hover:shadow-card-hover">
            <div className="text-xs uppercase tracking-wider text-ink-muted">{t.label}</div>
            <div className="mt-1 font-heading text-3xl font-bold text-ink">{t.value}</div>
            {t.sub && <div className="text-xs text-ink-muted">{t.sub}</div>}
          </Link>
        ))}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {labels.map((t) => (
          <Link key={t.to} to={t.to} className="flex items-center justify-between rounded-card-sm border border-border bg-white px-4 py-2.5 text-sm hover:bg-sand">
            <span className="text-ink-muted">{t.label}</span><span className="font-heading text-lg font-bold text-ink">{t.value}</span>
          </Link>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_380px]">
        <Card title="Sản phẩm cập nhật gần đây">
          {recent.length === 0 ? (
            <p className="text-sm text-ink-muted">Chưa có sản phẩm. <Link className="text-primary-700 underline" to="/products/new">Tạo sản phẩm đầu tiên</Link>.</p>
          ) : (
            <ul className="divide-y divide-border">
              {recent.map((p) => {
                const img = typeof p.images?.[0] === 'object' ? p.images[0] : null
                return (
                  <li key={p.id} className="flex items-center gap-3 py-2.5">
                    <div className="relative size-10 shrink-0 overflow-hidden rounded-image bg-sand">{img && <img src={img.sizes?.thumbnail?.url ?? img.url} alt="" className="size-full object-cover" />}<BadgeMask product={p} /></div>
                    <div className="min-w-0 flex-1">
                      <Link to={`/products/${p.id}`} className="block truncate text-sm font-medium text-ink hover:underline">{p.title}</Link>
                      <div className="text-xs text-ink-muted">{p.onSale && p.salePrice != null ? <><span className="text-warning">{formatVND(p.salePrice)}</span> <s>{formatVND(p.price)}</s></> : formatVND(p.price)}</div>
                    </div>
                    <Badge tone={p.published ? 'success' : 'neutral'}>{p.published ? 'Hiển thị' : 'Ẩn'}</Badge>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>
        {/*
          Lưới là 2 cột (`1fr 380px`). Thêm card thứ ba thành phần tử con trực tiếp là nó
          rơi xuống hàng dưới ở **cột trái** — cột phải bỏ trống. Gom hai card của cột phải
          vào một bọc.
        */}
        <div className="space-y-6">
          <Card title="Cần chú ý">
            <ul className="space-y-3 text-sm">
              <Warn g={stats.noImage} label="sản phẩm chưa có ảnh" tone="danger" />
              <Warn g={stats.noSeo} label="sản phẩm thiếu meta title hoặc description" />
              <Warn g={stats.outOfStock} label="sản phẩm hết hàng đang hiển thị" />
              <Warn g={stats.lowStock} label={`sản phẩm tồn thấp (≤ ${stats.lowStockThreshold})`} />
              <Warn g={stats.saleExpiringIn7Days} label="đợt giảm giá hết hạn trong 7 ngày" />
              {noWarn && <li className="text-ink-muted">Không có gì cần xử lý ✓</li>}
            </ul>
          </Card>

          <Card title="Xem nhiều nhất (30 ngày)">
            {top === null ? (
              <p className="text-sm text-ink-muted">Đang tải…</p>
            ) : top.length === 0 ? (
              <p className="text-sm text-ink-muted">
                Chưa có lượt xem nào được ghi nhận. Số đếm bằng JavaScript phía trình duyệt nên
                bot không tính, và chỉ tính từ khi tính năng này chạy.
              </p>
            ) : (
              <ol className="space-y-2.5 text-sm">
                {top.map((t) => (
                  <li key={`${t.type}:${t.id}`} className="flex items-baseline justify-between gap-3">
                    <Link
                      to={t.type === 'category' ? `/categories` : `/${t.type === 'product' ? 'products' : 'posts'}/${t.id}`}
                      className="truncate text-ink hover:text-primary-700"
                      title={t.title}
                    >
                      {t.title}
                    </Link>
                    <span className="shrink-0 font-heading text-sm text-ink-muted">{t.views.toLocaleString('vi-VN')}</span>
                  </li>
                ))}
              </ol>
            )}
          </Card>

        </div>
      </div>
    </>
  )
}

function Warn({ g, label, tone = 'warning' }: { g: StatGroup; label: string; tone?: 'warning' | 'danger' }) {
  if (!g.count) return null
  return (
    <li>
      <div className="flex items-center gap-2"><Badge tone={tone}>{g.count}</Badge><span className="text-ink">{label}</span></div>
      <ul className="mt-1 pl-1 text-xs text-ink-muted">
        {g.items.slice(0, 5).map((p) => <li key={p.id}>· <Link to={`/products/${p.id}`} className="hover:underline">{p.title}</Link></li>)}
        {g.count > 5 && <li className="text-ink-faint">… và {g.count - 5} sản phẩm khác</li>}
      </ul>
    </li>
  )
}
